import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { MainLayout } from '../layouts/MainLayout';
import { reportService, type ReportData, type RiskCategorySummary } from '../services/api/reportService';
import type { RiskCategory } from '../types';

const riskCategoryLabels: Record<RiskCategory, string> = {
  legal: '法的リスク',
  economic: '経済的リスク',
  financial: '財政的リスク',
  international: '国際的リスク',
  domestic_political: '国内政治的リスク',
  reputation: '対世論リスク',
  employee: '従業員リスク',
  labor_union: '労働組合リスク',
};

interface ReportOptions {
  format: 'pdf' | 'excel';
  includeCategories: RiskCategory[];
  includeUnchecked: boolean;
  includeMemo: boolean;
  includeTimestamp: boolean;
  includeSummary: boolean;
}

const maMethodLabels: Record<string, string> = {
  stock_transfer: '株式譲渡',
  business_transfer: '事業譲渡',
  merger: '合併',
  company_split: '会社分割',
  stock_exchange: '株式交換・移転',
};

const statusLabels: Record<string, string> = {
  planning: '検討中',
  in_progress: '進行中',
  completed: '完了',
  cancelled: '中止',
};

export function ReportPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const [options, setOptions] = useState<ReportOptions>({
    format: 'pdf',
    includeCategories: [],
    includeUnchecked: true,
    includeMemo: true,
    includeTimestamp: true,
    includeSummary: true,
  });

  // レポートデータ取得
  const loadReportData = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await reportService.getReportData(id);
      setReportData(data);

      // 取得したデータからリスクカテゴリを初期選択
      const categories = data.riskCategorySummary.map((s: RiskCategorySummary) => s.category);
      setOptions((prev) => ({
        ...prev,
        includeCategories: categories,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'レポートデータの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  const handleCategoryToggle = (category: RiskCategory) => {
    setOptions((prev) => ({
      ...prev,
      includeCategories: prev.includeCategories.includes(category)
        ? prev.includeCategories.filter((c) => c !== category)
        : [...prev.includeCategories, category],
    }));
  };

  const handleGenerateReport = async () => {
    if (!id || !reportData) return;

    setIsGenerating(true);
    setError(null);

    try {
      if (options.format === 'pdf') {
        // PDFレポート生成
        const blob = await reportService.generatePdfReport(id, options.includeSummary);

        // ファイル名を生成（案件名 + 日付）
        const date = new Date().toISOString().split('T')[0];
        const filename = `${reportData.project.name}_レポート_${date}`;

        // ダウンロード開始
        reportService.downloadReport(blob, filename, 'pdf');
      } else {
        // Excel形式は未実装のため、エラーメッセージを表示
        setError('Excel形式のレポート生成は現在準備中です。');
        return;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'レポート生成に失敗しました。もう一度お試しください。');
    } finally {
      setIsGenerating(false);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  const completionPercentage = reportData?.checkResultSummary
    ? Math.round((reportData.checkResultSummary.completed / reportData.checkResultSummary.total) * 100)
    : 0;

  return (
    <MainLayout>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/projects/${id}`)}
          >
            案件詳細に戻る
          </Button>
        </Box>

        <Typography variant="h5" component="h1" fontWeight={500} sx={{ mb: 3 }}>
          レポート生成
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Left Column: Options */}
          <Paper sx={{ flex: 1, p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              出力オプション
            </Typography>

            {/* Format Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>出力形式</InputLabel>
              <Select
                value={options.format}
                label="出力形式"
                onChange={(e) => setOptions({ ...options, format: e.target.value as 'pdf' | 'excel' })}
              >
                <MenuItem value="pdf">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PdfIcon color="error" />
                    PDF形式
                  </Box>
                </MenuItem>
                <MenuItem value="excel">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ExcelIcon color="success" />
                    Excel形式
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            {/* Category Selection */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              出力するリスクカテゴリ
            </Typography>
            <FormGroup sx={{ mb: 3 }}>
              {reportData?.riskCategorySummary.map((summary) => (
                <FormControlLabel
                  key={summary.category}
                  control={
                    <Checkbox
                      checked={options.includeCategories.includes(summary.category)}
                      onChange={() => handleCategoryToggle(summary.category)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{summary.categoryName || riskCategoryLabels[summary.category]}</span>
                      <Chip
                        label={`${summary.completed}/${summary.total}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  }
                />
              ))}
            </FormGroup>

            <Divider sx={{ my: 2 }} />

            {/* Additional Options */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              追加オプション
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.includeSummary}
                    onChange={(e) => setOptions({ ...options, includeSummary: e.target.checked })}
                  />
                }
                label="サマリーを含める"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.includeUnchecked}
                    onChange={(e) => setOptions({ ...options, includeUnchecked: e.target.checked })}
                  />
                }
                label="未確認項目を含める"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.includeMemo}
                    onChange={(e) => setOptions({ ...options, includeMemo: e.target.checked })}
                  />
                }
                label="メモを含める"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.includeTimestamp}
                    onChange={(e) => setOptions({ ...options, includeTimestamp: e.target.checked })}
                  />
                }
                label="タイムスタンプを含める"
              />
            </FormGroup>

            <Divider sx={{ my: 3 }} />

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={() => setShowPreview(true)}
                disabled={options.includeCategories.length === 0}
              >
                プレビュー
              </Button>
              <Button
                variant="contained"
                startIcon={isGenerating ? <CircularProgress size={20} /> : options.format === 'pdf' ? <PdfIcon /> : <ExcelIcon />}
                onClick={handleGenerateReport}
                disabled={isGenerating || options.includeCategories.length === 0}
              >
                {isGenerating ? '生成中...' : 'レポートを生成'}
              </Button>
            </Box>
          </Paper>

          {/* Right Column: Preview */}
          <Paper sx={{ width: 400, p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              案件概要
            </Typography>

            {reportData && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    案件名
                  </Typography>
                  <Typography variant="body2">{reportData.project.name}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    対象企業
                  </Typography>
                  <Typography variant="body2">{reportData.project.targetCompany}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    M&A手法
                  </Typography>
                  <Typography variant="body2">
                    {maMethodLabels[reportData.project.maMethod] || reportData.project.maMethod}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    ステータス
                  </Typography>
                  <Chip
                    label={statusLabels[reportData.project.status] || reportData.project.status}
                    size="small"
                    color="warning"
                  />
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    チェック進捗
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={completionPercentage}
                      sx={{ flex: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2" fontWeight={500}>
                      {completionPercentage}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {reportData.checkResultSummary.completed} / {reportData.checkResultSummary.total} 項目完了
                  </Typography>
                </Box>

                {showPreview && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        レポートプレビュー
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, bgcolor: 'grey.50', minHeight: 200 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          レポートには以下が含まれます：
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                          {options.includeSummary && <li>案件サマリー</li>}
                          {options.includeCategories.map((cat) => (
                            <li key={cat}>{riskCategoryLabels[cat]}</li>
                          ))}
                          {options.includeMemo && <li>チェック項目メモ</li>}
                          {options.includeTimestamp && <li>出力日時</li>}
                        </Box>
                      </Paper>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </MainLayout>
  );
}
